import React from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./lib/AuthContext";
import { LangProvider } from "./lib/LangContext";
import { ProtectedRoute, AdminRoute } from "./components/RouteGuards";
import Navbar from "./components/site/Navbar";
import Footer from "./components/site/Footer";
import CookieConsent from "./components/site/CookieConsent";

import Home from "./pages/Home";
import HowItWorks from "./pages/HowItWorks";
import Services from "./pages/Services";
import Examples from "./pages/Examples";
import Pricing from "./pages/Pricing";
import ForBusinesses from "./pages/ForBusinesses";
import Faq from "./pages/Faq";
import Contact from "./pages/Contact";
import Legal from "./pages/Legal";
import Login from "./pages/Login";
import GetStarted from "./pages/GetStarted";
import { ForgotPassword, ResetPassword } from "./pages/PasswordPages";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentPages";
import AuthCallback from "./pages/AuthCallback";
import NotFound from "./pages/NotFound";

import DashboardLayout from "./pages/dashboard/DashboardLayout";
import Overview from "./pages/dashboard/Overview";
import QuickSetup from "./pages/dashboard/QuickSetup";
import Create from "./pages/dashboard/Create";
import PhotoPost from "./pages/dashboard/PhotoPost";
import HowToPost from "./pages/dashboard/HowToPost";
import Campaigns from "./pages/dashboard/Campaigns";
import Brand from "./pages/dashboard/Brand";
import Connections from "./pages/dashboard/Connections";
import Publishing from "./pages/dashboard/Publishing";
import AutoPilot from "./pages/dashboard/AutoPilot";
import Insights from "./pages/dashboard/Insights";
import MyContent from "./pages/dashboard/MyContent";
import Tasks from "./pages/dashboard/Tasks";
import ContentCalendar from "./pages/dashboard/ContentCalendar";
import Promotions from "./pages/dashboard/Promotions";
import UploadMedia from "./pages/dashboard/UploadMedia";
import BrandProfile from "./pages/dashboard/BrandProfile";
import Requests from "./pages/dashboard/Requests";
import Subscription from "./pages/dashboard/Subscription";
import Billing from "./pages/dashboard/Billing";
import Support from "./pages/dashboard/Support";
import Settings from "./pages/dashboard/Settings";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminCustomerDetail from "./pages/admin/AdminCustomerDetail";
import AdminContent from "./pages/admin/AdminContent";
import AdminTasks from "./pages/admin/AdminTasks";
import AdminCalendar from "./pages/admin/AdminCalendar";
import AdminRevisions from "./pages/admin/AdminRevisions";
import AdminPromotions from "./pages/admin/AdminPromotions";
import {
  AdminBrandProfiles, AdminSubscriptions, AdminInvoices, AdminSupport, AdminAnalytics,
} from "./pages/admin/AdminPanels";

const PublicLayout = () => (
  <div className="flex min-h-screen flex-col">
    <Navbar />
    <div className="flex-1">
      <Outlet />
    </div>
    <Footer />
    <CookieConsent />
  </div>
);

function AppRouter() {
  const location = useLocation();
  // Process a returning Google sign-in FIRST, before any protected route runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/services" element={<Services />} />
              <Route path="/examples" element={<Examples />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/for-businesses" element={<ForBusinesses />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/legal/:slug" element={<Legal />} />
              <Route path="/login" element={<Login />} />
              <Route path="/get-started" element={<GetStarted />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Overview />} />
              <Route path="setup" element={<QuickSetup />} />
              <Route path="create" element={<Create />} />
              <Route path="photo" element={<PhotoPost />} />
              <Route path="how-to-post" element={<HowToPost />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="brand" element={<Brand />} />
              <Route path="connections" element={<Connections />} />
              <Route path="publishing" element={<Publishing />} />
              <Route path="autopilot" element={<AutoPilot />} />
              <Route path="insights" element={<Insights />} />
              <Route path="content" element={<MyContent />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="calendar" element={<ContentCalendar />} />
              <Route path="promotions" element={<Promotions />} />
              <Route path="media" element={<UploadMedia />} />
              <Route path="brand-profile" element={<BrandProfile />} />
              <Route path="requests" element={<Requests />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="billing" element={<Billing />} />
              <Route path="support" element={<Support />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<AdminOverview />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="customers/:id" element={<AdminCustomerDetail />} />
              <Route path="brand-profiles" element={<AdminBrandProfiles />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="tasks" element={<AdminTasks />} />
              <Route path="calendar" element={<AdminCalendar />} />
              <Route path="promotions" element={<AdminPromotions />} />
              <Route path="revisions" element={<AdminRevisions />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="analytics" element={<AdminAnalytics />} />
            </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <LangProvider>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </LangProvider>
  );
}
